import React, { useState, useEffect, useRef } from "react";
import {
  forwardToPurchaserMailTriggerFunction,
  getUserFollowers,
  searchUserByEmail,
} from "../../utils/actions/commanActions";
import { Button, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { LucidLoader } from "../Icons";
import { Modal as AntModal } from "antd";
import { validateEmail } from "../../utils/helper";

const ForwardToPurchaseModal = ({
  isForwardToPurchaserModalOpen,
  setIsForwardToPurchaserModalOpen,
  quoteId,
  selectedQuote,
  setSelectedQuote,
}) => {
  const [purchaserEmail, setPurchaserEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isPurchaserEmailExists, setIsPurchaserEmailExists] = useState(true);
  const [purchaserFirstName, setPurchaserFirstName] = useState("");
  const [purchaserLastName, setPurchaserLastName] = useState("");
  const [isPurchaserEmailLoading, setIsPurchaserEmailLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emailList, setEmailList] = useState([]);

  const ref = useRef(false);

  const token = localStorage.getItem("Token");
  const loggedInUserEmail = localStorage.getItem("email");

  useEffect(() => {
    if (!purchaserEmail || ref.current) {
      setIsPurchaserEmailExists(true);
      setEmailList([]);
      return;
    }

    setIsPurchaserEmailLoading(true);

    const delay = setTimeout(async () => {
      try {
        const response = await searchUserByEmail(
          token,
          purchaserEmail,
          selectedQuote?.userId?.companySiteId?.domain
        );

        if (response?.payload && response?.payload.length > 0) {
          setIsPurchaserEmailExists(true);

          // filter out already added followers
          const followers = selectedQuote?.followers || [];
          const filteredUsers = response.payload.filter(
            (user) => !followers.some((f) => f.email === user.email)
          );

          setEmailList(filteredUsers);
        } else {
          setIsPurchaserEmailExists(false);
          setEmailList([]);
        }
      } catch (error) {
        console.error("Error searching user by email:", error);
      } finally {
        setIsPurchaserEmailLoading(false);
      }
    }, 500);

    return () => clearTimeout(delay);
  }, [purchaserEmail, token, selectedQuote]);

  const handleSend = async () => {
    setErrorMessage("");

    if (!purchaserEmail || !validateEmail(purchaserEmail)) {
      setErrorMessage("Please enter a valid email address.");
      return;
    };

    //check if email is already exists in the list
    const isEmailExistsInList = selectedQuote?.followers.some(
      (item) => item.email === purchaserEmail
    );
    if (isEmailExistsInList) {
      setErrorMessage("This email is already added to the team.");
      return;
    }

    if (!isPurchaserEmailExists && !purchaserFirstName) {
      setErrorMessage("First name is required for new user.");
      return;
    };


    const isGenericCompany = selectedQuote?.userId?.companySiteId?.domain === "*";
    const enteredDomain = purchaserEmail.split("@")[1];
    const currentUserDomain = loggedInUserEmail.split("@")[1];

    if (!isGenericCompany && enteredDomain !== currentUserDomain) {
      setErrorMessage("Only email addresses associated with your company domain are allowed.");
      return;
    }

    setLoading(true);

    const response = await forwardToPurchaserMailTriggerFunction(token, {
      email: purchaserEmail,
      firstName: purchaserFirstName,
      lastName: purchaserLastName,
      quoteId,
    });

    if (!response?.success) {
      setErrorMessage(response?.message || "Unable to forward email to the purchaser at this time.");
      setLoading(false);
      setIsForwardToPurchaserModalOpen(false);
      return;
    }

    const updatedFollowers = await getUserFollowers(selectedQuote?._id);
    setSelectedQuote((prev) => ({
      ...prev,
      followers: updatedFollowers?.data || [],
    }));

    toast.success(`Email sent to ${purchaserEmail}`);
    setIsForwardToPurchaserModalOpen(false);
    setPurchaserEmail("");
    setPurchaserFirstName("");
    setPurchaserLastName("");
    setLoading(false);
  };

  return (
    <Modal
      size="sm"
      centered
      dialogClassName="qoute-version-modal"
      show={isForwardToPurchaserModalOpen}
      onHide={() => setIsForwardToPurchaserModalOpen(false)}
    >
      <Modal.Header closeButton>
        <Modal.Title className="text-[16px] font-semibold">Forward To Purchaser</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Enter Purchaser Email:</label>

          <div className="relative w-full">
            <input
              type="email"
              className="border rounded-lg p-2 w-full text-sm focus:ring-2 focus:ring-blue-500 shadow-sm"
              value={purchaserEmail}
              onChange={(e) => {
                if (!e.target.value) {
                  setIsPurchaserEmailLoading(false);
                }
                setPurchaserEmail(e.target.value);
                setErrorMessage("");
                ref.current = false;
              }}
              placeholder="Enter email here"
            />

            {/* Email suggestions list */}
            {emailList.length > 0 && (
              <ul className="absolute z-50 left-0 right-0 border rounded mt-1 bg-white shadow max-h-40 overflow-y-auto text-sm">
                {emailList.map((user) => (
                  <li
                    key={user.email}
                    className="px-3 py-2 cursor-pointer hover:bg-gray-100"
                    onClick={() => {
                      setPurchaserEmail(user.email);
                      setEmailList([]);
                      ref.current = true;
                    }}
                  >
                    {user.email}{" "}
                    <span className="text-xs text-gray-500">
                      ({user.firstName} {user.lastName})
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>


          {/* New user fields */}
          {!isPurchaserEmailExists && !isPurchaserEmailLoading && purchaserEmail && (
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm text-gray-700">First Name:</label>
                <input
                  type="text"
                  className="border rounded-lg p-2 w-full text-sm"
                  value={purchaserFirstName}
                  onChange={(e) => setPurchaserFirstName(e.target.value)}
                  placeholder="Enter first name"
                />
              </div>
              <div>
                <label className="text-sm text-gray-700">Last Name:</label>
                <input
                  type="text"
                  className="border rounded-lg p-2 w-full text-sm"
                  value={purchaserLastName}
                  onChange={(e) => setPurchaserLastName(e.target.value)}
                  placeholder="Enter last name"
                />
              </div>
            </div>
          )}

          {errorMessage && <p className="text-red-600 text-sm mt-2">{errorMessage}</p>}

          <button
            className="bg-primaryColor mt-4 flex items-center justify-center text-white px-3 py-1 text-sm rounded w-32 hover:bg-primaryColor-dark disabled:opacity-50"
            onClick={handleSend}
            disabled={loading || isPurchaserEmailLoading}
          >
            {isPurchaserEmailLoading ? "Checking..." : loading ? <LucidLoader /> : "Send"}
          </button>

          {/* Team members list */}
          <div className="mt-6">
            <h2 className="text-sm font-semibold mb-3">Purchase Members</h2>
            <table className="table-auto border-collapse border border-gray-400 w-full text-sm">
              <thead>
                <tr>
                  <th className="border border-gray-300 px-2 py-1">First Name</th>
                  <th className="border border-gray-300 px-2 py-1">Last Name</th>
                  <th className="border border-gray-300 px-2 py-1">Email</th>
                  <th className="border border-gray-300 px-2 py-1">Role</th>
                </tr>
              </thead>
              <tbody>
                {selectedQuote?.followers?.map((item, idx) => (
                  <tr key={idx}>
                    <td className="border border-gray-300 px-2 py-1">{item.firstName}</td>
                    <td className="border border-gray-300 px-2 py-1">{item.lastName || ""}</td>
                    <td className="border border-gray-300 px-2 py-1">{item.email}</td>
                    <td className="border border-gray-300 px-2 py-1">
                      {selectedQuote?.userId?._id === item?._id ? "Owner" : "Follower"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ForwardToPurchaseModal;
